import React, { useMemo } from 'react';
import styles from './index.module.scss';
import { useTranslation } from 'react-i18next';
import { isIE } from '@/util';
import { isGuest } from '@/util/commonUtils';
import { HEADER_LOGO_IMAGE_URL } from '@/util/headerLogo';
import { usePermissions } from '@/util/hooks/checkPermissions';
import { useSelector } from 'react-redux';
import LanguageDropdown from '@/components/LanguageDropdown';
import { NATION_LOGO_MAP } from '@/constants/nationLogoMap';

interface IAppProps {
  type?: any;
}

interface FooterLinkItem {
  key: string;
  label: string;
  onClick: () => void;
  highlight?: boolean;
}

const defaultLanguageMap: Record<string, string> = {
  zh: '简体中文',
  en: 'English',
  vi: 'Tiếng Việt',
  hu: 'Magyar',
  'pt-BR': 'Português (Brasil)',
  th: 'ไทย',
  id: 'Bahasa Indonesia',
  zh_Hant: '繁体中文',
  ru: 'Русский',
  de: 'Deutsch',
};

const NewFooter: React.FC<IAppProps> = () => {
  const { i18n } = useTranslation();
  const { checkPermission } = usePermissions();
  const userData = useSelector((state: any) => state?.User?.data?.[0] || {});
  const footerLogoSrc = HEADER_LOGO_IMAGE_URL;
  const userType = userData?.userType;
  const languageList = userData?.language || defaultLanguageMap;
  const C_customEmail = checkPermission('C_customEmail');
  const currentLanguage = i18n.language;
  const currentLanguageName =
    languageList?.[currentLanguage] || defaultLanguageMap?.[currentLanguage] || TLT('简体中文');
  const footerEmail =
    C_customEmail && typeof C_customEmail === 'string' && C_customEmail.trim()
      ? C_customEmail
      : 'org.jdi.service1@jd.com';

  const openPage = (url: string, target: '_self' | '_blank' = '_self') => {
    if (target === '_self' && isIE()) {
      window.location.href = url;
      return;
    }
    window._openPage(url, target);
  };

  const changeLanguage = (lang: string) => {
    if (lang === currentLanguage) return;
    window.localStorage.setItem('_language', lang);
    window.localStorage.setItem('iv', '');
    window.location.reload();
  };

  const quickLinks: FooterLinkItem[] = useMemo(() => {
    const links: FooterLinkItem[] = [
      {
        key: 'workspace',
        label: TLT('我的工作台'),
        onClick: () => openPage('/accountSettings'),
      },
      {
        key: 'batchCart',
        label: TLT('批量加入购物车'),
        onClick: () => openPage('/cart/batchAdd'),
      },
      {
        key: 'cart',
        label: TLT('购物车'),
        onClick: () => openPage('/cart/carts'),
      },
      {
        key: 'order',
        label: TLT('订单'),
        onClick: () => openPage(userType === 6 ? '/approvalList' : '/orderList'),
      },
    ];
    return links;
  }, [userType]);

  const aboutLinks: FooterLinkItem[] = [
    {
      key: 'companyProfile',
      label: TLT('公司简介'),
      onClick: () => openPage('//www.jingdongindustrials.com/', '_blank'),
    },
    // {
    //   key: 'privacy',
    //   label: TLT('隐私政策'),
    //   onClick: () => openPage('https://m-isp.jd.com/static/private_policy.html', '_blank'),
    // },
    // {
    //   key: 'terms',
    //   label: TLT('使用条款'),
    //   onClick: () => openPage('https://gw-mobile-isp.jd.com/protocol.html', '_blank'),
    // },
    {
      key: 'aboutUs',
      label: TLT('关于我们'),
      onClick: () => openPage('/about', '_blank'),
    },
    {
      key: 'afterSalesPolicy',
      label: TLT('售后政策'),
      // 售后政策页需登录态；未登录先跳登录页
      onClick: () => {
        if (isGuest()) {
          openPage('/passport/login', '_self');
          return;
        }
        openPage('/afterSalesPolicy', '_blank');
      },
    },
    {
      key: 'contact',
      label: TLT('联系我们'),
      onClick: () => openPage('/customerregistration', '_blank'),
    },
  ];

  return (
    <footer className={styles.footer}>
      <div className={styles.container}>
        <div className={styles.contentRow}>
          <div className={styles.brandColumn}>
            <img className={styles.logo} onClick={() => openPage('/')} src={footerLogoSrc} alt="JINGDONG Industrials" />
            <LanguageDropdown
              currentLanguage={currentLanguage}
              languageMap={{ ...languageList, [currentLanguage]: currentLanguageName }}
              nationLogoMap={NATION_LOGO_MAP}
              onChangeLanguage={changeLanguage}
              theme="dark"
              className={styles.languageDropdown}
              hideCurrentInMenu
            />
          </div>

          <div className={styles.linkColumn}>
            <div className={styles.columnTitle}>{TLT('快捷链接')}</div>
            {quickLinks.map(item => (
              <button
                key={item.key}
                className={`${styles.linkItem} ${styles.linkItemQuick}`}
                onClick={item.onClick}
                type="button"
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className={styles.linkColumn}>
            <div className={styles.columnTitle}>{TLT('关于京东工业')}</div>
            {aboutLinks.map(item => (
              <button
                key={item.key}
                className={`${styles.linkItem} ${styles.linkItemAbout} ${
                  item.highlight ? styles.linkItemHighlight : ''
                }`}
                onClick={item.onClick}
                type="button"
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className={`${styles.linkColumn} ${styles.contactColumn}`}>
            <div className={styles.columnTitle}>{TLT('联系方式')}</div>
            <a className={styles.contactItem} href={`mailto:${footerEmail}`}>
              <i className="iconFont icon-mail"></i>
              <span>{footerEmail}</span>
            </a>
            {/* <div className={`${styles.contactItem} ${styles.contactItemAddress}`}>
              <i className="iconFont icon-location"></i>
              <span className={styles.addressText}>
                <strong>{TLT('查找线下地址')}</strong>
                <span>{TLT('欧洲各地设有数百家门店/直营网点')}</span>
              </span>
            </div> */}
            {/* <button className={styles.locationButton} type="button" onClick={() => openPage('/about', '_blank')}>
              {TLT('查询位置')}
            </button> */}
          </div>
        </div>
        <div className={styles.copyright}>
          Copyright © 2004-2026 JD.COM. <span>{TLT('版权所有')}</span>
        </div>{' '}
      </div>
    </footer>
  );
};

export default NewFooter;
